import { execTargets, guessMainFile, MAX_CONFIG_BYTES, MAX_PACK_FILES, packFileFor, placeByExecs } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropZone } from "./DropZone.tsx";
import { Icon } from "./Icon.tsx";

const CFG_ACCEPT = [".cfg", ".txt", "text/plain"];

/** One file of a config being written: the first is the main file (its name may be empty: from the title). */
export interface PackFile {
  name: string;
  text: string;
}

/** The pack as the API takes it. */
export function packBody(files: PackFile[]) {
  const [main, ...rest] = files;
  return { text: main?.text ?? "", fileName: main?.name.trim() || undefined, files: rest.map((f) => ({ name: f.name.trim(), text: f.text })) };
}

/** The game's name for a picked file: "spawn_ak.cfg". */
const pickedName = (file: File) => file.name.replace(/\s+/g, "_");

/** Names the game will look for (exec lines) that the pack does not have. */
export function missingExecs(files: PackFile[], mainName: string): string[] {
  const names = [mainName, ...files.slice(1).map((f) => f.name)];
  const out = new Set<string>();
  for (const f of files) for (const n of execTargets(f.text)) if (!packFileFor(n, names)) out.add(n);
  return [...out];
}

/**
 * Writes a config as one file or as a pack: pick one file or several at once (the
 * one that runs the others becomes the main file), paste, add files by hand.
 * Tabs switch the file being edited; the parent shows `active` read line by line.
 */
export function PackEditor({
  files,
  onChange,
  active,
  onActive,
  mainHint,
}: {
  files: PackFile[];
  onChange: (files: PackFile[]) => void;
  active: number;
  onActive: (i: number) => void;
  /** The main file's name when none is typed (from the title). */
  mainHint: string;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const current = files[active] ?? files[0];
  const set = (i: number, patch: Partial<PackFile>) => onChange(files.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const pick = async (list: File[]) => {
    setError(null);
    if (list.length === 0) return;
    const picked: PackFile[] = [];
    for (const file of list) {
      if (file.size > MAX_CONFIG_BYTES) return setError(t("configs.tooBigNamed", { name: file.name }));
      const text = await file.text();
      if (text.includes("\u0000")) return setError(t("configs.notTextNamed", { name: file.name }));
      picked.push({ name: pickedName(file), text });
    }
    // Nothing written yet: the picked files are the pack. Otherwise they join it.
    const empty = files.length === 1 && !files[0].text.trim();
    const all = empty ? picked : [...files, ...picked.filter((p) => !files.some((f) => f.name.toLowerCase() === p.name.toLowerCase()))];
    if (all.length > MAX_PACK_FILES + 1) return setError(t("configs.tooManyFiles", { count: MAX_PACK_FILES + 1 }));
    const main = empty ? guessMainFile(all) : 0;
    const [first, ...rest] = [all[main], ...all.filter((_, i) => i !== main)];
    // The main file keeps its name; the others take the folder the exec lines give them.
    onChange([first, ...placeByExecs([first, ...rest]).slice(1)]);
    onActive(0);
  };
  const makeMain = (i: number) => {
    onChange([files[i], ...files.filter((_, j) => j !== i)]);
    onActive(0);
  };
  const remove = (i: number) => {
    const next = files.filter((_, j) => j !== i);
    onChange(next.length ? next : [{ name: "", text: "" }]);
    onActive(Math.max(0, Math.min(active, next.length - 1)));
  };
  const addEmpty = () => {
    let n = files.length;
    while (files.some((f) => f.name.toLowerCase() === `file_${n}.cfg`)) n++;
    onChange([...files, { name: `file_${n}.cfg`, text: "" }]);
    onActive(files.length);
  };
  const missing = files.length > 1 || execTargets(files[0]?.text ?? "").length > 0 ? missingExecs(files, files[0]?.name.trim() || mainHint) : [];

  return (
    <div className="pack-editor">
      <DropZone accept={CFG_ACCEPT} multiple hint={t("drop.configs")} onFiles={(files) => void pick(files)} onRefused={(names) => setError(t("drop.refused", { names: names.join(", ") }))}>
        <label className="field">
          <span>{t("configs.filesLabel")}</span>
          <input type="file" accept={CFG_ACCEPT.join(",")} multiple onChange={(e) => void pick(Array.from(e.target.files ?? [])).then(() => (e.target.value = ""))} />
          <span className="muted small">{t("configs.filesHelp")}</span>
        </label>
      </DropZone>
      {error && <p className="field-error small">{error}</p>}

      <div className="pack-tabs" role="tablist" aria-label={t("configs.packFiles")}>
        {files.map((f, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === active}
            className="pack-tab"
            onClick={() => onActive(i)}
          >
            <span className="mono">{(i === 0 ? f.name.trim() || mainHint : f.name) || t("configs.unnamed")}</span>
            {i === 0 && files.length > 1 && <span className="pack-main-tag">{t("configs.mainTag")}</span>}
          </button>
        ))}
        {files.length <= MAX_PACK_FILES && (
          <button type="button" className="btn btn-quiet btn-small" onClick={addEmpty}>
            <Icon name="plus" size={14} />
            {t("configs.addFile")}
          </button>
        )}
      </div>

      <div className="pack-file" role="tabpanel">
        <div className="pack-file-head">
          <label className="field pack-name">
            <span>{active === 0 ? t("configs.mainNameLabel") : t("configs.fileNameLabel")}</span>
            <input
              className="mono"
              value={current.name}
              placeholder={active === 0 ? mainHint : "spawn/ak.cfg"}
              onChange={(e) => set(active, { name: e.target.value })}
              maxLength={80}
              spellCheck={false}
            />
          </label>
          {files.length > 1 && (
            <div className="row">
              {active !== 0 && (
                <button type="button" className="btn btn-quiet btn-small" onClick={() => makeMain(active)}>
                  {t("configs.makeMain")}
                </button>
              )}
              <button type="button" className="btn btn-quiet btn-small btn-danger" onClick={() => remove(active)}>
                {t("configs.removeFile")}
              </button>
            </div>
          )}
        </div>
        <label className="field">
          <span className="visually-hidden">{t("configs.textOf", { name: current.name || mainHint })}</span>
          <textarea
            className="mono cfg-text"
            rows={12}
            value={current.text}
            aria-label={t("configs.textOf", { name: current.name || mainHint })}
            onChange={(e) => set(active, { text: e.target.value })}
            placeholder={'cl_crosshairsize "2"\nbind "mouse4" "+jump"'}
            spellCheck={false}
          />
        </label>
      </div>
      {missing.length > 0 && <p className="banner banner-info small">{t("configs.missingExecs", { names: missing.join(", ") })}</p>}
    </div>
  );
}
